import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { currentUserHasRole, getCurrentUser } from '../../utils/roleCheck';
import './UserManagement/UserManagement.css';

function UserManagementPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    accountId: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    roleIds: [],
    isActive: true
  });
  
  // Roles and accounts
  const [roles, setRoles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  
  // Delete confirmation
  const [deleteNumbers, setDeleteNumbers] = useState(['', '', '']);
  const [deleteError, setDeleteError] = useState(null);

  // Check authorization on mount
  useEffect(() => {
    if (!currentUserHasRole('UserAdmin')) {
      navigate('/user-home');
    }
  }, [navigate]);

  // Load initial data
  useEffect(() => {
    loadUsers();
    loadRoles();
    loadAccounts();
  }, [currentPage, pageSize]);

  // Filter users based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(user =>
        user.firstName?.toLowerCase().includes(term) ||
        user.lastName?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const user = getCurrentUser();
      const response = await apiService.getUsersList(currentPage, pageSize, user?.companyId);
      
      if (response.success) {
        setUsers(response.users || []);
        setTotalCount(response.totalCount || 0);
        setTotalPages(response.totalPages || 0);
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await apiService.getRoles();
      if (response.success) {
        setRoles(response.roles || []);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const loadAccounts = async () => {
    try {
      const user = getCurrentUser();
      const response = await apiService.getAccounts(user?.companyId);
      if (response.success) {
        setAccounts(response.accounts || []);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  };

  const handleCreateUser = () => {
    setFormData({
      accountId: '',
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      roleIds: [],
      isActive: true
    });
    setShowCreateModal(true);
  };

  const handleEditUser = async (user) => {
    try {
      console.log('Fetching user details for:', user.userId);
      const response = await apiService.getUserDetails(user.userId);
      console.log('API Response:', response);
      
      if (response && response.success && response.user) {
        const userData = response.user;
        console.log('User data:', userData);
        setSelectedUser(userData);
        setFormData({
          accountId: userData.accountId,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          password: '',
          roleIds: userData.roles?.map(r => r.roleId) || [],
          isActive: userData.isActive
        });
        setShowEditModal(true);
      } else {
        console.error('Invalid response structure:', response);
        setError('Failed to load user details - invalid response format');
      }
    } catch (err) {
      console.error('Error loading user details:', err);
      setError('Failed to load user details');
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteNumbers(['', '', '']);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (showCreateModal) {
        const response = await apiService.createUser(formData);
        if (response.success) {
          setSuccess('User created successfully');
          setShowCreateModal(false);
          loadUsers();
        }
      } else if (showEditModal) {
        const response = await apiService.updateUser(selectedUser.userId, formData);
        if (response.success) {
          setSuccess('User updated successfully');
          setShowEditModal(false);
          loadUsers();
        }
      }
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.error || 'Failed to save user');
    }
  };

  const handleConfirmDelete = async () => {
    // Validate odd numbers
    const nums = deleteNumbers.map(n => parseInt(n));
    const isValid = nums.every(n => 
      !isNaN(n) && n >= 1 && n <= 99 && n % 2 !== 0
    ) && new Set(nums).size === 3;

    if (!isValid) {
      setDeleteError('Please enter 3 different odd numbers between 1-99');
      return;
    }

    try {
      const response = await apiService.deleteUser(selectedUser.userId);
      if (response.success) {
        setSuccess('User deleted successfully');
        setShowDeleteModal(false);
        setDeleteError(null);
        setDeleteNumbers(['', '', '']);
        loadUsers();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setDeleteError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleRoleToggle = (roleId) => {
    setFormData(prev => {
      const roleIds = prev.roleIds.includes(roleId)
        ? prev.roleIds.filter(id => id !== roleId)
        : [...prev.roleIds, roleId];
      return { ...prev, roleIds };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && users.length === 0) {
    return (
      <div className="user-management">
        <div className="user-management__loading">
          <div className="user-management__loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      {/* Header */}
      <div className="user-management__header">
        <h1 className="user-management__title">User Management</h1>
        <button 
          className="user-management__create-btn"
          onClick={handleCreateUser}
        >
          + Create New User
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="user-management__message user-management__message--error">
          {error}
        </div>
      )}
      {success && (
        <div className="user-management__message user-management__message--success">
          {success}
        </div>
      )}

      {/* Search Bar */}
      <div className="user-management__search">
        <input
          type="text"
          className="user-management__search-input"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="user-management__search-clear"
            onClick={() => setSearchTerm('')}
          >
            ×
          </button>
        )}
      </div>

      {/* User Table */}
      <div className="user-management__table-container">
        {filteredUsers.length === 0 ? (
          <div className="user-management__empty">
            <div className="user-management__empty-icon">👥</div>
            <p className="user-management__empty-text">
              {searchTerm ? 'No users found matching your search' : 'No users found'}
            </p>
          </div>
        ) : (
          <table className="user-management__table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.userId}>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`user-management__status-badge ${
                      user.isActive 
                        ? 'user-management__status-badge--active' 
                        : 'user-management__status-badge--inactive'
                    }`}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{formatDate(user.lastLoginAt)}</td>
                  <td>{formatDate(user.createDate)}</td>
                  <td>
                    <div className="user-management__actions">
                      <button
                        className="user-management__btn user-management__btn--edit"
                        onClick={() => handleEditUser(user)}
                      >
                        Edit
                      </button>
                      <button
                        className="user-management__btn user-management__btn--delete"
                        onClick={() => handleDeleteUser(user)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!searchTerm && totalCount > 0 && (
        <div className="user-management__pagination">
          <div className="user-management__pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
          </div>
          <div className="user-management__pagination-controls">
            <button
              className="user-management__pagination-btn"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, idx) => {
              const page = idx + 1;
              return (
                <button
                  key={page}
                  className={`user-management__pagination-btn ${
                    currentPage === page ? 'user-management__pagination-btn--active' : ''
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              className="user-management__pagination-btn"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <div className="user-management__page-size">
              <label>Per page:</label>
              <select
                className="user-management__page-size-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="user-management__modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
        }}>
          <div className="user-management__modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-management__modal-header">
              <h2 className="user-management__modal-title">
                {showCreateModal ? 'Create New User' : 'Edit User'}
              </h2>
            </div>
            <form className="user-management__form" onSubmit={handleFormSubmit}>
              {/* Account */}
              <div className="user-management__form-group">
                <label className="user-management__label user-management__label--required">
                  Account
                </label>
                <select
                  className="user-management__select"
                  value={formData.accountId}
                  onChange={(e) => setFormData({...formData, accountId: e.target.value})}
                  required
                  disabled={showEditModal}
                >
                  <option value="">Select Account</option>
                  {accounts.map(acc => (
                    <option key={acc.companyAccountId} value={acc.companyAccountId}>
                      {acc.accountName}
                    </option>
                  ))}
                </select>
              </div>

              {/* First Name */}
              <div className="user-management__form-group">
                <label className="user-management__label user-management__label--required">
                  First Name
                </label>
                <input
                  type="text"
                  className="user-management__input"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </div>

              {/* Last Name */}
              <div className="user-management__form-group">
                <label className="user-management__label user-management__label--required">
                  Last Name
                </label>
                <input
                  type="text"
                  className="user-management__input"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </div>

              {/* Email */}
              <div className="user-management__form-group">
                <label className="user-management__label user-management__label--required">
                  Email
                </label>
                <input
                  type="email"
                  className="user-management__input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>

              {/* Password (only for create) */}
              {showCreateModal && (
                <div className="user-management__form-group">
                  <label className="user-management__label user-management__label--required">
                    Password
                  </label>
                  <input
                    type="password"
                    className="user-management__input"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                  />
                </div>
              )}

              {/* Roles */}
              <div className="user-management__form-group">
                <label className="user-management__label user-management__label--required">
                  Roles
                </label>
                <div className="user-management__checkbox-group">
                  {roles.map(role => (
                    <div key={role.roleId} className="user-management__checkbox-item">
                      <input
                        type="checkbox"
                        id={`role-${role.roleId}`}
                        className="user-management__checkbox"
                        checked={formData.roleIds.includes(role.roleId)}
                        onChange={() => handleRoleToggle(role.roleId)}
                      />
                      <label 
                        htmlFor={`role-${role.roleId}`}
                        className="user-management__checkbox-label"
                      >
                        {role.roleName}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Is Active (only for edit) */}
              {showEditModal && (
                <div className="user-management__form-group">
                  <label className="user-management__label">Status</label>
                  <div className="user-management__toggle">
                    <div 
                      className={`user-management__toggle-switch ${
                        formData.isActive ? 'user-management__toggle-switch--active' : ''
                      }`}
                      onClick={() => setFormData({...formData, isActive: !formData.isActive})}
                    >
                      <div className={`user-management__toggle-slider ${
                        formData.isActive ? 'user-management__toggle-slider--active' : ''
                      }`} />
                    </div>
                    <span>{formData.isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="user-management__form-actions">
                <button
                  type="button"
                  className="user-management__form-btn user-management__form-btn--secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="user-management__form-btn user-management__form-btn--primary"
                >
                  {showCreateModal ? 'Create User' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="user-management__modal-overlay" onClick={() => {
          setShowDeleteModal(false);
          setDeleteError(null);
          setDeleteNumbers(['', '', '']);
        }}>
          <div className="user-management__modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-management__modal-header">
              <h2 className="user-management__modal-title">Delete User</h2>
            </div>
            <div className="user-management__delete-warning">
              <p className="user-management__delete-warning-text">
                ⚠️ You are about to delete {selectedUser?.firstName} {selectedUser?.lastName}. This action cannot be undone.
              </p>
            </div>
            {deleteError && (
              <div className="user-management__message user-management__message--error" style={{marginBottom: '1rem'}}>
                {deleteError}
              </div>
            )}
            <p>To confirm deletion, enter 3 different odd numbers (1-99):</p>
            <div className="user-management__delete-inputs">
              {[0, 1, 2].map(idx => (
                <input
                  key={idx}
                  type="number"
                  className="user-management__delete-input"
                  value={deleteNumbers[idx]}
                  onChange={(e) => {
                    const newNumbers = [...deleteNumbers];
                    newNumbers[idx] = e.target.value;
                    setDeleteNumbers(newNumbers);
                  }}
                  min="1"
                  max="99"
                  placeholder={`#${idx + 1}`}
                />
              ))}
            </div>
            <div className="user-management__form-actions">
              <button
                className="user-management__form-btn user-management__form-btn--secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteError(null);
                  setDeleteNumbers(['', '', '']);
                }}
              >
                Cancel
              </button>
              <button
                className="user-management__form-btn user-management__form-btn--primary"
                onClick={handleConfirmDelete}
                style={{background: '#ef4444'}}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagementPage;
